import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosError, AxiosResponse } from "axios"
import qs from "qs"
import { useInfiniteQuery, useQuery } from "react-query"
import { ITeacherTransactionsFilterData } from "ts/interfaces/Transaction"
import {
  getNextPageSkip,
  MATCHING_IDS_QUERY_OPTIONS,
  serializeQueryParams,
} from "utils/reactQuery"

import {
  IAccountTransactions,
  IAccountTransactionsArgs,
  IHasTransactions,
  IHasTransactionsArgs,
  IStudentAllTransactions,
  IStudentAllTransactionsArgs,
  IInfiniteTeacherTransactionsArgs,
  ITeacherTransactions,
  ITeacherTransactionsFilterDataArgs,
  ITeacherTransactionsIdsArgs,
} from "./transaction.types"

export const useQueryAllTransactions = (args: IStudentAllTransactionsArgs) =>
  useQuery<AxiosResponse<IStudentAllTransactions[]>, AxiosError>(
    ["allTransactions"],
    async () =>
      axiosInstance(
        Endpoint.STUDENTS_ID_ALL_TRANSACTIONS.replace(
          "ID",
          `${args.student_id}`
        ),
        {
          params: {
            skip: args.page,
            limit: args.limit,
            pending: args.is_pending,
            type_in: args.type,
          },
          paramsSerializer: (params) => {
            return qs.stringify(params, { indices: false })
          },
        }
      ),
    {
      ...args?.options,
    }
  )

export const useInfiniteTeacherTransactions = (
  args: IInfiniteTeacherTransactionsArgs
) =>
  useInfiniteQuery<AxiosResponse<ITeacherTransactions[]>, AxiosError>(
    ["teacherTransactions", args.filters],
    async ({ pageParam = 0 }) =>
      axiosInstance(Endpoint.TEACHER_TRANSACTIONS, {
        params: { ...args.filters, skip: pageParam, limit: args.limit },
        paramsSerializer: serializeQueryParams,
      }),
    {
      ...args.options,
      getNextPageParam: getNextPageSkip(args.limit),
    }
  )

export const useQueryTeacherTransactionsIds = (
  args: ITeacherTransactionsIdsArgs
) =>
  useQuery<AxiosResponse<number[]>, AxiosError>(
    ["teacherTransactionsIds", args.filters],
    async () =>
      axiosInstance(Endpoint.TEACHER_TRANSACTIONS_IDS, {
        params: args.filters,
        paramsSerializer: serializeQueryParams,
      }),
    { ...MATCHING_IDS_QUERY_OPTIONS, ...args.options }
  )

export const useQueryTeacherTransactionsFilterData = (
  args?: ITeacherTransactionsFilterDataArgs
) =>
  useQuery<AxiosResponse<ITeacherTransactionsFilterData>, AxiosError>(
    ["teacherTransactionsFilterData"],
    async () => axiosInstance(Endpoint.TEACHER_TRANSACTIONS_FILTER_DATA),
    { ...args?.options }
  )

export const useQueryAccountTransactions = (args?: IAccountTransactionsArgs) =>
  useQuery<AxiosResponse<IAccountTransactions[]>, AxiosError>(
    [
      "account_transactions",
      args?.transaction_type,
      args?.start_date,
      args?.end_date,
      args?.skip,
      args?.saving_account_id,
      args?.transaction_filter,
    ],
    async () => {
      return axiosInstance(Endpoint.STUDENT_ACCOUNT_TRANSACTIONS, {
        params: {
          skip: args?.skip,
          start_date: args?.start_date,
          end_date: args?.end_date,
          transaction_type: args?.transaction_type,
          saving_account_id: args?.saving_account_id,
          transaction_filter: args?.transaction_filter,
        },
      })
    },
    {
      ...args?.options,
    }
  )

export const useQueryHasTransactions = (args?: IHasTransactionsArgs) =>
  useQuery<AxiosResponse<IHasTransactions>, AxiosError>(
    ["hasTransactions"],
    async () => {
      return axiosInstance(Endpoint.STUDENT_HAS_TRANSACTIONS)
    },
    args?.options
  )
